/**
 * Per-session prompt hook (TanStack Query).
 *
 * Fetches GET /sessions/{id}/prompt — the system prompt the session runs on,
 * broken down by the layer that contributed each part (base / role / personal /
 * session / …). The prompt is rebuilt every turn, so this is the view as of
 * the read, and a reopened drawer reads it again.
 *
 * LAZY by design: this powers an opt-in transparency drawer that defaults
 * CLOSED. The query stays disabled until the drawer is opened (`enabled`), so a
 * clinician who never taps the affordance never triggers the request. Modelled
 * on useSessionQueryLogs (the lazily-enabled pattern).
 */

import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { sessionsApi } from '@/api/endpoints/sessions';
import type { PromptPreviewResponse } from '@/api/types_session';
import { ApiError } from '@/api/client';

export function useSessionPrompt(sessionId: string | null, enabled: boolean) {
  return useAuthenticatedQuery<PromptPreviewResponse, ApiError>({
    queryKey: ['session', sessionId, 'prompt'],
    queryFn: () => sessionsApi.prompt(sessionId as string),
    enabled: !!sessionId && enabled,
    refetchOnWindowFocus: false,
  });
}
